import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { logger } from 'src/app/utils';

export interface ErrorLogContext {
  /* Where the log was triggered from, e.g. 'http-interceptor' | 'startup-health'. */
  source: string;
  /* The backend URL that failed, when applicable. */
  requestUrl?: string;
  /* The raw error (HttpErrorResponse, Error, ...). */
  error?: any;
}

/*
* Sends client-side error reports to the public log endpoint (environment.logUrl)
* for debugging issues that never reach our backend logs (e.g. status 0 / CORS /
* iframe blocking on the booking widget).
*
* Uses navigator.sendBeacon with a text/plain body so the request is a "simple"
* CORS request (no preflight) and is far more likely to actually leave the browser
* even when normal XHR is being blocked. Falls back to fetch(keepalive, no-cors).
* Logging must never throw or interfere with the app.
* */
@Injectable({ providedIn: 'root' })
export class LogService {
  /*
  * Builds a human-readable, pretty-printed JSON report. Used both as the body
  * sent to logUrl and as the text the user can copy from the error dialog.
  * */
  buildReport(context: ErrorLogContext): string {
    return JSON.stringify(this.buildPayload(context), null, 2);
  }

  logError(context: ErrorLogContext): void {
    if (!environment.logUrl) return;
    try {
      const payload = this.buildReport(context);
      const blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });

      const sent = typeof navigator !== 'undefined'
        && typeof navigator.sendBeacon === 'function'
        && navigator.sendBeacon(environment.logUrl, blob);

      if (!sent) {
        fetch(environment.logUrl, {
          method: 'POST',
          body: payload,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          keepalive: true,
          mode: 'no-cors',
        }).catch(() => { /* ignore */ });
      }
    } catch (e) {
      logger.w('Failed to send error log', e);
    }
  }

  private buildPayload(context: ErrorLogContext) {
    const nav: any = typeof navigator !== 'undefined' ? navigator : {};
    const now = new Date();
    return {
      timestampUtc: now.toISOString(),
      timezone: this.safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
      source: context.source,
      page: {
        href: this.safe(() => window.location.href),
        referrer: this.safe(() => document.referrer),
      },
      browser: {
        userAgent: nav.userAgent,
        language: nav.language,
        languages: nav.languages,
        vendor: nav.vendor,
        platform: nav.platform,
        userAgentData: nav.userAgentData ? {
          brands: nav.userAgentData.brands,
          mobile: nav.userAgentData.mobile,
          platform: nav.userAgentData.platform,
        } : undefined,
      },
      device: {
        screen: this.safe(() => ({ width: window.screen.width, height: window.screen.height })),
        viewport: this.safe(() => ({ width: window.innerWidth, height: window.innerHeight })),
        devicePixelRatio: this.safe(() => window.devicePixelRatio),
        online: nav.onLine,
        connection: nav.connection ? {
          effectiveType: nav.connection.effectiveType,
          downlink: nav.connection.downlink,
          rtt: nav.connection.rtt,
        } : undefined,
      },
      request: { url: context.requestUrl },
      error: this.serializeError(context.error),
    };
  }

  /*
  * HttpErrorResponse exposes most of its data via non-enumerable getters, so a
  * plain JSON.stringify of it yields {}. We extract the relevant fields explicitly.
  * `error` is the raw payload (for status 0 this is typically {isTrusted:true}).
  * */
  private serializeError(error: any) {
    if (!error) return undefined;
    return {
      name: error.name,
      message: error.message,
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      ok: error.ok,
      error: this.toPlainObject(error.error),
    };
  }

  private toPlainObject(value: any): any {
    if (value == null || typeof value !== 'object') return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return String(value);
    }
  }

  private safe<T>(fn: () => T): T | undefined {
    try {
      return fn();
    } catch {
      return undefined;
    }
  }
}
